import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { LangToggle } from "@/components/LangToggle";
import { BRAND } from "@/data/translations";
import { Menu, X } from "lucide-react";

const NAV_LINK_CLASS =
    "shrink-0 whitespace-nowrap font-display text-[10px] min-[1100px]:text-[11px] min-[1280px]:text-xs xl:text-[13px] uppercase tracking-[0.12em] min-[1280px]:tracking-[0.16em] xl:tracking-[0.2em] transition-colors";

const CTA_BUTTON_CLASS =
    "bg-fw text-black font-display font-bold text-[10px] min-[1280px]:text-xs xl:text-sm px-3 min-[1280px]:px-4 xl:px-5 py-2 rounded-sm hover:bg-fw-glow hover:glow-green transition-all whitespace-nowrap uppercase tracking-wider";

const buildNavLinks = (t) => [
    { id: "method", label: t.nav.method, href: "/#method" },
    { id: "protocols", label: t.nav.protocols, href: "/#protocols" },
    { id: "bio", label: t.nav.bio, href: "/#bio" },
    { id: "testimonials", label: t.nav.testimonials, href: "/#testimonials" },
    { id: "risorse", label: t.nav.resources, href: "/#risorse" },
    { id: "insights", label: t.nav.insights, href: "/#insights" },
    {
        id: "fueling-hub",
        label: t.nav.fuelingHub,
        href: "https://fit-fuel-calc-1.emergent.host",
        external: true,
    },
    { id: "contact", label: t.nav.contact, href: "/#contact" },
];

const NavLink = ({ link }) =>
    link.external ? (
        <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`nav-link-${link.id}`}
            className={`${NAV_LINK_CLASS} text-fw hover:text-fw-glow`}
        >
            {link.label}
        </a>
    ) : (
        <a
            href={link.href}
            data-testid={`nav-link-${link.id}`}
            className={`${NAV_LINK_CLASS} text-white/90 hover:text-fw`}
        >
            {link.label}
        </a>
    );

const MobileMenu = ({ navLinks, ctaLabel, onClose }) => (
    <div
        data-testid="mobile-menu"
        className="lg:hidden bg-black border-t border-white/10 animate-fade-in"
    >
        <div className="px-5 py-8 flex flex-col gap-5">
            {navLinks.map((link) =>
                link.external ? (
                    <a
                        key={link.id}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                        data-testid={`mobile-nav-link-${link.id}`}
                        className="font-display text-3xl font-medium tracking-tight text-fw hover:text-fw-glow transition-colors"
                    >
                        {link.label}
                    </a>
                ) : (
                    <a
                        key={link.id}
                        href={link.href}
                        onClick={onClose}
                        data-testid={`mobile-nav-link-${link.id}`}
                        className="font-display text-3xl font-medium tracking-tight text-white hover:text-fw transition-colors"
                    >
                        {link.label}
                    </a>
                )
            )}
            <a
                href="/#contact"
                onClick={onClose}
                data-testid="mobile-nav-cta"
                className={`mt-4 inline-flex items-center justify-center ${CTA_BUTTON_CLASS} py-3`}
            >
                {ctaLabel}
            </a>
        </div>
    </div>
);

export const Navigation = () => {
    const { t } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const navLinks = buildNavLinks(t);

    return (
        <header
            data-testid="site-navigation"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-black/90 border-b border-white/10"
                    : "bg-black/70 backdrop-blur-md"
            }`}
        >
            <nav className="mx-auto max-w-[1920px] px-4 lg:px-6 xl:px-8">
                <div className="flex items-center justify-between h-14 lg:h-16">
                    {/* LEFT: Logo */}
                    <Link
                        to="/"
                        data-testid="brand-logo"
                        className="shrink-0 flex items-center group"
                    >
                        <picture>
                            <source srcSet={BRAND.logoDarkWebp} type="image/webp" />
                            <img
                                src={BRAND.logoDark}
                                alt="Fit & Wellness — Luca La Capria"
                                className="h-8 lg:h-9 xl:h-10 w-auto select-none transition-transform duration-500 group-hover:scale-105"
                                draggable="false"
                                width="1100"
                                height="320"
                                fetchPriority="high"
                                decoding="async"
                            />
                        </picture>
                    </Link>

                    {/* CENTER: Nav Links (visible ≥1024px) */}
                    <div className="hidden lg:flex items-center justify-center flex-1 min-w-0 px-3">
                        <ul className="flex items-center flex-nowrap gap-x-2 min-[1280px]:gap-x-4 xl:gap-x-5 2xl:gap-x-6">
                            {navLinks.map((link) => (
                                <li key={link.id} className="shrink-0 whitespace-nowrap">
                                    <NavLink link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* RIGHT: Language toggle + CTA (visible ≥1024px) */}
                    <div className="hidden lg:flex items-center gap-2 min-[1280px]:gap-3 xl:gap-4 shrink-0">
                        <LangToggle testid="language-toggle" />
                        <a
                            href="/#contact"
                            data-testid="nav-cta-button"
                            className={CTA_BUTTON_CLASS}
                        >
                            {t.nav.cta}
                        </a>
                    </div>

                    {/* HAMBURGER: only <1024px */}
                    <button
                        onClick={() => setMenuOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                        className="lg:hidden p-2 -mr-2 text-white"
                    >
                        {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </nav>

            {menuOpen && (
                <MobileMenu
                    navLinks={navLinks}
                    ctaLabel={t.nav.cta}
                    onClose={() => setMenuOpen(false)}
                />
            )}
        </header>
    );
};
